// Types
import {
  ParkingFormData,
  LoungeFormData,
  LoungeFormWithFlightDetails
} from '@white-label-types/component';
import type {
  SearchCriteria,
  productTypeId,
} from '@white-label-types/parking-booking';
import type { CustomerGroup } from '@white-label-types/product-types';
import { getAppVariable } from '@white-label-helper/get-app-variable';

const convertChildAgeToNumbersArray = (childAges: string[] | null): number[]=> {
  if (!Array.isArray(childAges)) return [];

  const numbersArray = childAges.map(age => parseInt(age, 10));

  if (numbersArray.some(number => isNaN(number))) {
      return [];
  }

  return numbersArray;
}
export const parseCustomerGroups = (customerGroups?: CustomerGroup, defaultOneAdult?: boolean) => {
  if (customerGroups === undefined || customerGroups === null) {
    return defaultOneAdult ? { adult: 1 } : {};
  }

  const atLeastOneCustomerExists = (() => {
    if (!customerGroups) return false;
    return Object.keys(customerGroups)
      .filter((key) => key === 'adult' || key === 'senior')
      .some((key) => customerGroups[key]! > 0);
  })();

  if (defaultOneAdult && !atLeastOneCustomerExists) {
    return { adult: 1 };
  }
  return {
    ...Object.keys(customerGroups)
      .filter(
        (key) => {
          // @ts-expect-error-key issue with signature
          return customerGroups[key] > 0 && key !== 'children_ages'
        }
      )
      .reduce((res, key) => {
        // @ts-expect-error-key issue with signature
        if (Array.isArray(customerGroups[key])) {
          // @ts-expect-error-key issue with signature
          return Object.assign(res, { [key]: customerGroups[key] });
        } else {
          // @ts-expect-error-key issue with signature
          return Object.assign(res, { [key]: parseInt(customerGroups[key]) });
        }
      }, {})
  };
};

const additionalCriteria = {
  parking: (
    formData: ParkingFormData,
    productSearchCriteria: {
      date1?: string;
      time1?: string;
      date2?: string;
      time2?: string;
    }
  ) => {
    if (formData.customers) {
      Object.assign(productSearchCriteria, {
        groups: parseCustomerGroups(formData.customers),
      });
    }
  },
  lounges: (
    formData: LoungeFormData,
    productSearchCriteria: {
      date1?: string;
      time1?: string;
      date2?: string;
      time2?: string;
    }
  ) => {
    if (!formData.exitDate) {
      delete productSearchCriteria.date2;
      delete productSearchCriteria.time2;
    }
    if (!formData.entryDate) {
      delete productSearchCriteria.date1;
      delete productSearchCriteria.time1;
    }

    Object.assign(productSearchCriteria, {
      groups: parseCustomerGroups(formData.customers, true),
    });

    if (Array.isArray(formData.customers?.['children_ages']) && formData.customers?.['children_ages'].length) {
      Object.assign(productSearchCriteria, {
        children_ages: convertChildAgeToNumbersArray(formData.customers?.['children_ages'])
      });
    }

    const privateLoungeAirlinesList = getAppVariable(
      'products.lounges.airlines_list',
      []
    );
    const isPrivateLounge = privateLoungeAirlinesList.length > 0;

    if (isPrivateLounge) {
      if (formData.airline) {
        Object.assign(productSearchCriteria, {
          airline: formData.airline,
        });
      }

      if (formData.baggage_type) {
        Object.assign(productSearchCriteria, {
          baggage_type: formData.baggage_type,
        });
      }
    }
  },
  fasttrack: (
    formData: LoungeFormData,
    productSearchCriteria: {
      date1?: string;
      time1?: string;
      date2?: string;
      time2?: string;
    }
  ) => {
    if (!formData.exitDate) {
      delete productSearchCriteria.date2;
      delete productSearchCriteria.time2;
    }

    if (!formData.entryDate) {
      delete productSearchCriteria.date1;
      delete productSearchCriteria.time1;
    }

    Object.assign(productSearchCriteria, {
      groups: parseCustomerGroups(formData.customers, true),
    });

    if (Array.isArray(formData.customers?.['children_ages']) && formData.customers?.['children_ages'].length) {
      Object.assign(productSearchCriteria, {
        children_ages: convertChildAgeToNumbersArray(formData.customers?.['children_ages'])
      });
    }
  },
};

const isTimeslotRequest = (
  formData: ParkingFormData | LoungeFormData | LoungeFormWithFlightDetails
): formData is LoungeFormWithFlightDetails => 'flightDetails' in formData;

export function createSearchCriteria(
  formData: ParkingFormData | LoungeFormData | LoungeFormWithFlightDetails,
  token: string,
  productId: productTypeId = 'parking',
  language: string
): SearchCriteria {
  const productSearchCriteria = {
    airport_code: formData.airport || getAppVariable('poi').code,
    date1: formData.entryDate || '',
    time1: formData.entryTime?.value || '',
    date2: formData?.exitDate || '',
    time2: formData?.exitTime?.value || '',
    language: language,
    is_primary: getAppVariable('primary_product_type') === productId,
  };
  if (formData.terminal) {
    Object.assign(productSearchCriteria, { terminal: +formData.terminal });
  }
  if (formData.discount) {
    Object.assign(productSearchCriteria, { coupon_code: formData.discount });
  }
  if (isTimeslotRequest(formData)) {
    Object.assign(productSearchCriteria, {
      flight_number: formData.flightDetails.flightNumber,
      airline_iata: formData.flightDetails.airlineIATA,
      airline_icao: formData.flightDetails.airlineICAO,
      schedule_time: formData.flightDetails.scheduleTime,
      provider_product_code: formData.providerProductCode,
    });
  }
  if (formData?.outboundFlight) {
    Object.assign(productSearchCriteria, {
      flight_number: formData.outboundFlight.flightNumber,
      airline_iata: formData.outboundFlight.airlineIATA,
      is_flight_selected: true,
    });
  }
  if (typeof additionalCriteria[productId] === 'function') {
    additionalCriteria[productId](formData, productSearchCriteria);
  }
  if (formData.utmSource || formData.utmMedium) {
    Object.assign(productSearchCriteria, {
      marketing_channels: {
        ...(formData.utmSource ? { utm_source: formData.utmSource } : {}),
        ...(formData.utmMedium ? { utm_medium: formData.utmMedium } : {}),
      }
    })
  }

  const searchCriteria =  {
    token,
    customer_id: null,
    criteria: {
      [productId]: productSearchCriteria,
    },
  };

  if(formData.meta) {
    Object.assign(searchCriteria, {meta: formData.meta});
  }
  return searchCriteria;
}
